import { useEffect, useLayoutEffect, useState } from "react";
import { X } from "lucide-react";
import { useT } from "@/i18n";
import { useGuide } from "@/lib/guide/context";

const PRIMARY_LABEL = {
  navigate: (t, key) => t(`guide.theme.${key}.cta`),
  next: (t) => t("guide.ui.next"),
  finish: (t) => t("guide.ui.done"),
  stripe: (t) => t("guide.theme.payments.cta"),
  outlook: (t) => t("guide.theme.calendar.cta"),
};

function useSpotlight(selector, active, stepIndex) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!selector) { setRect(null); return; }
    let raf, tries = 0;
    const find = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          return;
        }
      }
      tries += 1;
      if (tries < 60) raf = requestAnimationFrame(find);
      else setRect(null);
    };
    find();
    return () => raf && cancelAnimationFrame(raf);
  }, [selector, active, stepIndex]);

  useEffect(() => {
    if (!selector) return undefined;
    const onMove = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => { window.removeEventListener("resize", onMove); window.removeEventListener("scroll", onMove, true); };
  }, [selector]);

  return rect;
}

export function GuideOverlay() {
  const t = useT();
  const { active, step, stepIndex, stepCount, primaryAction, later, skipTheme, closeOverlay } = useGuide();
  const rect = useSpotlight(step?.target, active, stepIndex);
  if (!active || !step) return null;

  const pad = 8;
  const hole = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  // Tooltip placement: below the spotlight if room, else above; centred if no rect.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const ttWidth = Math.min(380, vw - 32);
  let ttStyle;
  if (hole) {
    const below = hole.top + hole.height + 14;
    const placeBelow = below + 220 < vh;
    const top = placeBelow ? below : Math.max(16, hole.top - 232);
    let left = hole.left + hole.width / 2 - ttWidth / 2;
    left = Math.max(16, Math.min(left, vw - ttWidth - 16));
    ttStyle = { top, left, width: ttWidth };
  } else {
    ttStyle = { top: vh / 2 - 120, left: vw / 2 - ttWidth / 2, width: ttWidth };
  }

  const primaryLabel = (PRIMARY_LABEL[step.primary] || PRIMARY_LABEL.next)(t, active);

  return (
    <div className="fixed inset-0 z-[70]" data-testid="guide-overlay" style={{ pointerEvents: "none" }}>
      {hole ? (
        <div
          className="absolute rounded-2xl transition-all duration-300"
          style={{
            top: hole.top, left: hole.left, width: hole.width, height: hole.height,
            boxShadow: "0 0 0 9999px rgba(28, 25, 23, 0.55)",
            outline: "2px solid rgba(255,255,255,0.85)",
            outlineOffset: 2,
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: "rgba(28,25,23,0.55)" }} />
      )}

      <div
        className="absolute rounded-2xl border border-border bg-card p-5 shadow-2xl animate-fade-in"
        style={{ ...ttStyle, pointerEvents: "auto" }}
        data-testid="guide-tooltip"
      >
        <button
          onClick={closeOverlay}
          data-testid="guide-overlay-close"
          className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label={t("guide.ui.close")}
        >
          <X className="h-4 w-4" />
        </button>

        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary/80">
          {t("guide.ui.step", { x: stepIndex + 1, y: stepCount })}
        </p>
        <h3 className="mt-1 font-display text-lg font-semibold text-foreground">{t(`guide.theme.${active}.title`)}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground" data-testid="guide-step-text">
          {t(`guide.theme.${active}.step${stepIndex + 1}`)}
        </p>

        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${((stepIndex + 1) / stepCount) * 100}%` }} />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={primaryAction}
            data-testid="guide-primary"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {primaryLabel}
          </button>
          {step.secondary === "skip" ? (
            <button onClick={skipTheme} data-testid="guide-skip" className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("guide.ui.not_now")}
            </button>
          ) : (
            <button onClick={later} data-testid="guide-later" className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("guide.ui.later")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
